<template>
    <DashboardContentLayout :heading="$t('views.monitoring.groupOverview.headings.title')" has-pig-type-picker slim-pig-type-picker>
        <template #header-top-right>
            <!-- Page-local v-model, not has-period-picker's shared store.state.periodPickerDateString
                 (~45 other pages) - this page defaults to 12 months instead of that picker's own
                 3-month default, which must not leak into what those other pages see. -->
            <PeriodRangeDropdown v-model:value="periodDateString" />
        </template>
        <div class="border-t border-t-gray-200 mt-5 mb-6" aria-hidden="true" />
        <div class="flex items-center justify-between flex-wrap gap-4">
            <Select v-if="filteredData.length > 1" :model-value="jumpToFarmId" @update:model-value="handleJumpToFarm">
                <SelectTrigger class="w-56">
                    <SelectValue :placeholder="$t('views.monitoring.groupOverview.fields.jumpToFarm.placeholder')" />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem v-for="farm in filteredData" :key="farm.farm_id" :value="String(farm.farm_id)">{{ farm.farm_name }}</SelectItem>
                </SelectContent>
            </Select>
            <div v-else />
            <div class="flex items-center flex-wrap gap-4">
                <DropdownMenu :modal="false">
                    <DropdownMenuTrigger as-child>
                        <Button
                            variant="outline"
                            class="transition-[opacity,background-color]"
                            :class="{ 'visible opacity-100': selectedProductionGroupIds.length > 0, 'invisible opacity-0': selectedProductionGroupIds.length === 0 }"
                        >
                            {{ $t('views.monitoring.groupOverview.buttons.manageSelected') }}
                            <span class="bg-gray-100 min-w-5 flex items-center justify-center rounded-[5px] px-1.5 py-px font-semibold">{{ selectedProductionGroupIds.length }}</span>
                            <ChevronDown class="size-4" aria-hidden="true" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent class="min-w-[var(--radix-dropdown-menu-trigger-width)]" align="end">
                        <DropdownMenuItem class="cursor-pointer" @click="barnSelections = {}">{{ $t('views.monitoring.groupOverview.buttons.unselectAll') }}</DropdownMenuItem>
                        <DropdownMenuItem class="cursor-pointer" @click="handleBulkDelete">
                            <span class="text-red-500">{{ $t('views.monitoring.groupOverview.buttons.delete') }}</span>
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
                <div class="cursor-pointer" :class="cn(buttonVariants({ variant: 'outline' }))" @click.self="showOnlyFavorites = !showOnlyFavorites">
                    <Checkbox id="unread" v-model:checked="showOnlyFavorites" />
                    <label for="unread" class="cursor-pointer select-none text-normal font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                        {{ $t('views.monitoring.groupOverview.fields.showOnlyFavorites') }}
                    </label>
                </div>
            </div>
        </div>
        <FadeTransition mode="out-in">
            <div v-if="isLoading" class="flex items-center justify-center px-4 py-16">
                <Spinner class="h-10 w-10" />
            </div>
            <div v-else-if="filteredData.length" key="content" class="max-w-full space-y-5 mt-12">
                <div v-for="farm in filteredData" :key="farm.farm_id" :ref="(el) => setFarmCardRef(farm.farm_id, el)" class="border border-gray-300 rounded-lg px-4 py-5 scroll-mt-24">
                    <div class="flex items-center justify-between space-x-4">
                        <div class="flex items-center space-x-3">
                            <div class="size-6">
                                <Icon class="h-full w-full object-contain" :src="IconSource.FarmsPrimary"></Icon>
                            </div>
                            <div>
                                <div class="font-bold text-lg">{{ farm.farm_name }}</div>
                                <div class="text-sm text-gray-500">
                                    {{ farm.barns.length }} {{ farm.barns.length === 1 ? $t('views.monitoring.groupOverview.texts.barn') : $t('views.monitoring.groupOverview.texts.barns') }},
                                    {{ farm.groupCount }}
                                    {{ farm.groupCount === 1 ? $t('views.monitoring.groupOverview.texts.group') : $t('views.monitoring.groupOverview.texts.groups') }}
                                </div>
                            </div>
                        </div>
                        <SearchInput
                            :model-value="farmSearchQueries[farm.farm_id] ?? ''"
                            :aria-label="$t('views.monitoring.groupOverview.fields.search.label')"
                            :placeholder="$t('views.monitoring.groupOverview.fields.search.placeholder')"
                            class="w-64"
                            @update:model-value="farmSearchQueries[farm.farm_id] = $event"
                            @keyup.esc="farmSearchQueries[farm.farm_id] = ''"
                        />
                    </div>
                    <div class="mt-5 space-y-5">
                        <BarnSection
                            v-for="barn in farm.barns"
                            :key="barn.barn_id"
                            :barn="barn"
                            :search-active="!!(farmSearchQueries[farm.farm_id] ?? '').trim()"
                            :manual-view-mode="barnViewModeOverrides[barn.barn_id]"
                            @create="handleCreate"
                            @edit="handleEdit"
                            @backfill="handleBackfill"
                            @update:manual-view-mode="barnViewModeOverrides[barn.barn_id] = $event"
                        />
                    </div>
                </div>
            </div>
            <div v-else class="flex items-center justify-center px-4 py-16">
                <div class="text-gray-500 text-center">
                    {{ $t('views.monitoring.groupOverview.texts.noData') }}
                </div>
            </div>
        </FadeTransition>
        <CreateGroupModal v-if="barnToCreateFrom" v-model="showCreateModal" :barn="barnToCreateFrom" @chosen="handleSectionChosen" />
        <BackfillWizardModal v-if="barnToBackfillFrom" v-model="showBackfillModal" :barn="barnToBackfillFrom" :initial-section-id="backfillInitialSectionId" />
        <ManageProductionGroupModal v-model="isManageModalOpen" :target="manageTarget" :production-group="manageProductionGroup" allow-delete @delete="handleDeleteFromManageModal" />
        <DeleteGroupModal v-model="showDeleteModal" :group-ids="selectedGroupIds" @deleted="isManageModalOpen = false" />
    </DashboardContentLayout>
</template>

<script setup lang="ts">
import { format, parseISO, subMonths } from 'date-fns';
import { computed, ref, nextTick, provide, watch } from 'vue';
import { ChevronDown } from 'lucide-vue-next';
import DashboardContentLayout from '@/components/layouts/dashboardLayout/content/DashboardContentLayout.vue';
import { Barn, IconSource, ProductionGroup, ProductionGroupBarnResponseEntry } from '@/types';
import { store } from '@/plugins/store';
import { useBarnProductionGroups } from '@/plugins/store/actions/queries/production-groups';
import { useSectionById } from './useSectionById';
import BarnSection from './BarnSection.vue';
import PeriodRangeDropdown from '@/components/ui/PeriodRangeDropdown.vue';
import DeleteGroupModal from './DeleteGroupModal.vue';
import CreateGroupModal from './CreateGroupModal.vue';
import BackfillWizardModal from './BackfillWizardModal.vue';
import ManageProductionGroupModal, { type ManageProductionGroupTarget } from '../production-groups/ManageProductionGroupModal.vue';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { buttonVariants, Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import FadeTransition from '@/components/transitions/FadeTransition.vue';
import Spinner from '@/components/ui/Spinner.vue';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

const sitesAreLoaded = computed(() => store.state.sitesAreLoaded);
const barnIds = computed(() => store.getters.getSelectedSiteIdsFilteredByPigType);

// This page's own date range - deliberately NOT the global header PeriodPicker/store value shared
// by ~60 other pages (its 3-month default was too narrow to get an overview of groups, and widening
// it there would've widened every other page's default too). Sticky per-browser via its own
// localStorage key; defaults to the last 12 months so most barns' group history is visible at once.
const GROUP_OVERVIEW_PERIOD_STORAGE_KEY = 'group-overview-period';

function readStoredPeriod(): string {
    try {
        const stored = window.localStorage.getItem(GROUP_OVERVIEW_PERIOD_STORAGE_KEY);
        if (stored && stored.includes(' - ')) {
            return stored;
        }
    } catch {
        // localStorage unavailable (private browsing etc.) - fall through to default.
    }
    const today = new Date();
    return `${subMonths(today, 12).toISOString()} - ${today.toISOString()}`;
}

const periodDateString = ref(readStoredPeriod());

watch(periodDateString, (newValue) => {
    try {
        window.localStorage.setItem(GROUP_OVERVIEW_PERIOD_STORAGE_KEY, newValue);
    } catch {
        // ignore write failures
    }
});

// Same `from=yyyy-MM-dd&to=yyyy-MM-dd` shape the global picker's getSelectedPeriodByDate getter
// produces - GetProductionGroups takes this as an opaque query-string fragment either way.
const selectedPeriod = computed(() => {
    const [from, to] = periodDateString.value.split(' - ');
    return `from=${format(parseISO(from), 'yyyy-MM-dd')}&to=${format(parseISO(to), 'yyyy-MM-dd')}`;
});

const isQueryEnabled = computed(() => barnIds.value.length > 0);

const { data: productionGroupBarns, isLoading: isProductionGroupsLoading } = useBarnProductionGroups({ barnIds, selectedPeriod }, { enabled: isQueryEnabled });
const isLoading = computed(() => isProductionGroupsLoading.value || !sitesAreLoaded.value);

const showOnlyFavorites = ref(false);
// One search box per Farm card (see template) rather than one global box - each filters only its own
// Farm's Barns/groups, so typing in one card can't hide rows in another.
const farmSearchQueries = ref<Record<number, string>>({});

// Each Barn's manual All-groups/By-section choice, lifted up here (rather than owned locally in
// BarnSection) because a Weaners/Slaughter pigs toggle can drop a Barn out of `filteredData` entirely
// while the other species is selected and bring it back - unmounting/remounting that BarnSection
// instance, which would otherwise discard a local ref's choice. Keyed by barn_id so it survives that.
const barnViewModeOverrides = ref<Record<number, 'all' | 'bySection'>>({});

// Every Farm card is always rendered (see filteredData below), so on an account with many Farms the
// one you want can sit well below the fold - this is a one-shot "scroll to" action, not a filter, so
// it doesn't narrow what's on the page the way the per-card search or the global header picker do.
const farmCardRefs = new Map<number, HTMLElement>();

function setFarmCardRef(farmId: number, el: Element | { $el?: Element } | null) {
    const node = el && '$el' in el ? el.$el : el;

    if (node instanceof HTMLElement) {
        farmCardRefs.set(farmId, node);
    } else {
        farmCardRefs.delete(farmId);
    }
}

const jumpToFarmId = ref<string | undefined>(undefined);

function handleJumpToFarm(value: string) {
    // Commit the pick first - Select is controlled off this ref, and the underlying Radix combobox
    // latches its displayed value from this prop actually changing. Resetting straight to `undefined`
    // without ever writing `value` in between is a no-op from its perspective (undefined -> undefined),
    // so the trigger would keep showing the picked farm's name indefinitely instead of reverting.
    jumpToFarmId.value = value;
    farmCardRefs.get(Number(value))?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    nextTick(() => {
        jumpToFarmId.value = undefined;
    });
}

const data = computed(() => {
    const farms: {
        farm_id: number;
        farm_name: string;
        barns: (ProductionGroupBarnResponseEntry & { farm_id: number; barn_name: string })[];
        groupCount: number;
    }[] = [];

    const barns =
        productionGroupBarns.value
            ?.map((b) => {
                const barn = store.state.barns.find((stateBarn) => stateBarn.barn_id === b.barn_id);

                if (!barn) {
                    return null;
                }

                const productionGroups = b.production_groups
                    ?.filter((sp) => (showOnlyFavorites.value ? sp.is_favorite : true))
                    .map((sp) => ({
                        ...sp,
                        // Parsed to Date for `$d()` display, though ProductionGroup types them `string` - the
                        // convention BarnSection documents (it casts back at each use), so cast to match its prop.
                        start: (sp.start ? parseISO(sp.start as string) : null) as unknown as string,
                        end: (sp.end ? parseISO(sp.end as string) : null) as unknown as string,
                    }));

                return {
                    ...b,
                    farm_id: barn.farm_id,
                    production_groups: productionGroups,
                    barn_name: barn.name,
                };
            })
            .filter((b): b is NonNullable<typeof b> => b !== null) || [];

    for (const farm of store.state.farms) {
        const farmBarns = barns.filter((b) => b.farm_id === farm.farm_id && store.state.sections?.some((s) => s.barn_id === b.barn_id));

        if (farmBarns.length) {
            farms.push({
                farm_id: farm.farm_id,
                farm_name: farm.name,
                barns: farmBarns,
                groupCount: farmBarns.map((b) => b.production_groups.length).reduce((a, b) => a + b, 0),
            });
        }
    }

    return farms;
});

const sectionById = useSectionById();

// Matches a group's own name or the name of the Section it's located in - a farmer scanning the
// Location column and typing what they see should get a hit, not just a name-only match.
function matchesSearch(group: { name: string; location_id: number }, query: string): boolean {
    const locationName = sectionById.value.get(group.location_id)?.name ?? '';
    return group.name.toLowerCase().includes(query) || locationName.toLowerCase().includes(query);
}

// Filters each Farm's own Barns down to matching groups, but never drops a Barn or Farm card
// outright - a zero-match search renders as each Barn's table showing its own empty state (same
// as the page-level favorites filter), instead of the whole card silently vanishing.
const filteredData = computed(() =>
    data.value.map((farm) => {
        const query = (farmSearchQueries.value[farm.farm_id] ?? '').trim().toLowerCase();

        if (!query) {
            return farm;
        }

        return {
            ...farm,
            barns: farm.barns.map((barn) => ({
                ...barn,
                production_groups: barn.production_groups.filter((sp) => matchesSearch(sp, query)),
            })),
        };
    })
);

const showDeleteModal = ref(false);
const selectedGroupIds = ref<number[]>([]);

async function handleDelete(groupId: number) {
    selectedGroupIds.value = [groupId];
    await nextTick();
    showDeleteModal.value = true;
}

async function handleBulkDelete() {
    selectedGroupIds.value = getSelectedProductionGroups().map((sp) => sp.id);
    await nextTick();
    showDeleteModal.value = true;
}

const showCreateModal = ref(false);
const barnToCreateFrom = ref<Barn | null>(null);

async function handleCreate(barnId: number) {
    const barn = store.state.barns.find((b) => b.barn_id === barnId) || null;

    if (!barn) {
        return;
    }

    barnToCreateFrom.value = barn;
    await nextTick();
    showCreateModal.value = true;
}

// Resolves the Farm/Barn/Section names the shared manage modal displays as context, from whichever
// ids the caller already has (an existing group's, or the Barn + Section just chosen for a new one).
function resolveManageTarget(barnId: number, sectionId: number, productionGroupId: number | null): ManageProductionGroupTarget | null {
    const section = sectionById.value.get(sectionId);
    const barn = store.state.barns.find((b) => b.barn_id === barnId);

    if (!section || !barn) {
        return null;
    }

    const farm = store.state.farms.find((f) => f.farm_id === barn.farm_id);

    return {
        id: sectionId,
        sectionName: section.name,
        farmName: farm?.name ?? '',
        barnName: barn.name,
        barnId,
        productionGroupId,
    };
}

const isManageModalOpen = ref(false);
const manageTarget = ref<ManageProductionGroupTarget | null>(null);
const manageProductionGroup = ref<ProductionGroup | null>(null);

async function handleSectionChosen(sectionId: number) {
    const barnId = barnToCreateFrom.value?.barn_id;
    const target = barnId != null ? resolveManageTarget(barnId, sectionId, null) : null;

    if (!target) {
        return;
    }

    manageTarget.value = target;
    manageProductionGroup.value = null;
    await nextTick();
    isManageModalOpen.value = true;
}

async function handleDeleteFromManageModal(groupId: number) {
    isManageModalOpen.value = false;
    await handleDelete(groupId);
}

const showBackfillModal = ref(false);
const barnToBackfillFrom = ref<Barn | null>(null);
// Set when Backfill is launched from a Section's own expanded panel (Groups overview's By-section
// view) rather than the Barn-level button - pre-fills the wizard's Section picker so the farmer
// doesn't have to re-pick the Section they were already looking at.
const backfillInitialSectionId = ref<number | null>(null);

async function handleBackfill(barnId: number, sectionId: number | null = null) {
    const barn = store.state.barns.find((b) => b.barn_id === barnId) || null;

    if (!barn) {
        return;
    }

    barnToBackfillFrom.value = barn;
    backfillInitialSectionId.value = sectionId;
    await nextTick();
    showBackfillModal.value = true;
}

async function handleEdit(group: ProductionGroup) {
    // `group` comes from the table row, whose dates were parsed to `Date` objects for `$d()` display
    // (see `data` above) - the raw wire object (string dates) is what the manage modal needs, so it's
    // looked up fresh from the query result rather than trusted from the row.
    const rawGroup = productionGroupBarns.value?.flatMap((b) => b.production_groups ?? []).find((g) => g.id === group.id) ?? group;
    const target = resolveManageTarget(rawGroup.parent_location_id, rawGroup.location_id, rawGroup.id);

    if (!target) {
        return;
    }

    manageTarget.value = target;
    manageProductionGroup.value = rawGroup;
    await nextTick();
    isManageModalOpen.value = true;
}

const barnSelections = ref<Record<number, Record<number, boolean>>>({});

watch(
    () => productionGroupBarns.value,
    (barns) => {
        barnSelections.value = {};

        if (barns) {
            for (const barn of barns) {
                if (!barnSelections.value[barn.barn_id]) {
                    barnSelections.value[barn.barn_id] = {};
                }
            }
        }
    }
);

function handleSelectionChange(barnId: number, selections: Record<number, boolean>) {
    barnSelections.value[barnId] = selections;
}

provide('barnSelections', {
    selections: barnSelections,
    updateSelections: handleSelectionChange,
});

const selectedProductionGroupIds = computed(() =>
    Object.entries(barnSelections.value)
        .filter(([barnId]) => productionGroupBarns.value?.some((b) => b.barn_id.toString() === barnId))
        // eslint-disable-next-line no-unused-vars
        .flatMap(([barnId, selections]) => Object.keys(selections))
);

function getSelectedProductionGroups() {
    return productionGroupBarns.value?.flatMap((b) => b.production_groups?.filter((sp) => selectedProductionGroupIds.value.includes(sp.id.toString())) || []) || [];
}
</script>
